import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Project {
    id: number;
    title: string;
    category: string;
    image: string;
    description: string;
    technologies: string[];
    link?: string;
}

const projects: Project[] = [
    {
        id: 1,
        title: 'Bousanm',
        category: 'web',
        image: '/projects/bousanm.webp',
        description: "Plateforme fintech innovante basée au Cap-Haïtien, offrant des solutions de paiement numérique et de transfert d'argent sécurisées pour faciliter les transactions financières en Haïti.",
        technologies: ['React', 'Django Rest Framework', 'MySQL'],
        link: 'https://bousanm.com',
    },
    {
        id: 2,
        title: 'Bousanm Mobile',
        category: 'mobile',
        image: '/projects/bousanm-mobile.webp',
        description: "Application mobile de la plateforme Bousanm, permettant aux utilisateurs d'effectuer des transactions financières, de gérer leur portefeuille numérique et d'accéder à leurs services financiers en toute mobilité.",
        technologies: ['React Native', 'Django Rest Framework', 'MySQL'],
        link: '#',
    },
    {
        id: 3,
        title: 'Systeme de Gestion du Lycée National François Cappoix de Limonade',
        category: 'web',
        image: '/projects/lycee.webp',
        description: 'Système de gestion du Lycée National François Cappoix de Limonade',
        technologies: ['React', 'Django Rest Framework', 'MySQL'],
        link: 'https://lnfcl.com/',
    },
    // {
    //     id: 4,
    //     title: 'Sécurité Bancaire',
    //     category: 'security',
    //     image: '/projects/security.jpg',
    //     description: 'Solution de sécurité pour une institution financière majeure',
    //     technologies: ['Python', 'Blockchain', 'AI'],
    //     link: '#',
    // },
    // {
    //     id: 5,
    //     title: 'Système de Surveillance IP',
    //     category: 'security',
    //     image: '/projects/surveillance.jpg',
    //     description: 'Installation et configuration de caméras IP avec accès à distance',
    //     technologies: ['IP Cameras', 'NVR', 'Remote Access'],
    //     link: '#',
    // },
    // {
    //     id: 6,
    //     title: 'Gestion Hôtelière',
    //     category: 'web',
    //     image: '/projects/hotel.jpg',
    //     description: 'Système complet de gestion hôtelière avec réservation en ligne',
    //     technologies: ['Vue.js', 'Laravel', 'MySQL'],
    //     link: '#',
    // },
    // {
    //     id: 7,
    //     title: 'App Livraison',
    //     category: 'mobile',
    //     image: '/projects/delivery.jpg',
    //     description: 'Application de livraison avec suivi en temps réel',
    //     technologies: ['Flutter', 'Google Maps', 'Firebase'],
    //     link: '#',
    // },
    // {
    //     id: 8,
    //     title: 'Réseau Entreprise',
    //     category: 'cloud',
    //     image: '/projects/network.jpg',
    //     description: 'Infrastructure réseau complète pour une entreprise de 200+ employés',
    //     technologies: ['Cisco', 'VPN', 'Firewall'],
    //     link: '#',
    // },
    // {
    //     id: 9,
    //     title: 'Système Point de Vente',
    //     category: 'web',
    //     image: '/projects/pos.jpg',
    //     description: 'Solution POS moderne pour restaurants et commerces',
    //     technologies: ['Angular', 'Node.js', 'PostgreSQL'],
    //     link: '#',
    // },
    // {
    //     id: 10,
    //     title: 'App Gestion Stock',
    //     category: 'mobile',
    //     image: '/projects/inventory.jpg',
    //     description: 'Application mobile de gestion d\'inventaire avec scanner code-barres',
    //     technologies: ['React Native', 'SQLite', 'Barcode Scanner'],
    //     link: '#',
    // },
];

const categories = [
    { id: 'all', name: 'Tous' },
    { id: 'web', name: 'Web' },
    { id: 'mobile', name: 'Mobile' },
    { id: 'cloud', name: 'Cloud' },
    { id: 'security', name: 'Sécurité' },
];

const ProjectCard: React.FC<{ project: Project }> = ({ project }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative overflow-hidden rounded-xl shadow-lg bg-white dark:bg-gray-800"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative aspect-video overflow-hidden">
                <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-300 transform hover:scale-110"
                />
                <motion.div
                    initial={false}
                    animate={{ opacity: isHovered ? 1 : 0 }}
                    className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
                >
                    <div className="text-white text-center">
                        <p className="text-sm mb-2">{project.description}</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {project.technologies.map((tech) => (
                                <span
                                    key={tech}
                                    className="px-2 py-1 text-xs bg-blue-600 rounded-full"
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
            <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{project.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{project.category}</p>
            </div>
        </motion.div>
    );
};

const PortfolioSection = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const filteredProjects = projects.filter(
        (project) => selectedCategory === 'all' || project.category === selectedCategory
    );

    return (
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        Nos Réalisations
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Découvrez nos projets innovants qui transforment le paysage technologique haïtien
                    </p>
                </motion.div>

                <div className="flex flex-wrap justify-center gap-4 mb-12">
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${selectedCategory === category.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700'
                                }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>

                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <AnimatePresence>
                        {filteredProjects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
};

export default PortfolioSection;